import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Form,
  Input,
  Select,
  Radio,
  Button,
  Card,
  Upload,
  Row,
  Col,
  message,
  Tooltip,
  Alert,
  TimePicker,
} from 'antd';
import { QuestionCircleOutlined, UploadOutlined, CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PersianDatePicker from '../../PersianDatePicker';
import { employeesApi, leavesApi } from '../../../utils/api';
import { handleFormSubmitError } from '../../../utils/errorHandler';
import {
  LEAVE_DURATION_OPTIONS,
  buildLeavePayload,
  needsDateRange,
  needsSingleDate,
  needsTimeRange,
  toApiDate,
  toApiTime,
  toLatinDigits,
} from '../../../utils/leaveDurationUtils';

const { Option } = Select;
const { TextArea } = Input;

const formatFaNum = (n) => {
  if (n == null || n === '') return '—';
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  return num.toLocaleString('fa-IR');
};

const LeavePreviewBox = ({ preview, duration, loading, policy }) => {
  if (loading) {
    return (
      <Alert
        type="info"
        showIcon
        className="custom-font"
        message="در حال محاسبه روزهای کاری و سهمیه..."
      />
    );
  }
  if (!preview && !policy) return null;

  const excludedOffs = Array.isArray(preview?.excluded_day_offs) ? preview.excluded_day_offs : [];
  const excludedHolidays = Array.isArray(preview?.excluded_holidays) ? preview.excluded_holidays : [];
  const policies = preview?.policies || policy || {};
  const remaining =
    preview?.quota_remaining ?? preview?.leaves_remaining ?? policy?.remaining;
  const afterLeave = preview?.quota_after_leave;

  return (
    <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 my-1 custom-font">
      <h4 className="font-bold text-blue-900 dark:text-blue-200 mb-2 m-0 text-sm">خلاصه وضعیت مرخصی</h4>

      {duration === 'hourly' && preview ? (
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
          مدت زمان: <strong>{formatFaNum(preview.total_hours)} ساعت</strong>
          {' '}
          (معادل <strong>{formatFaNum(preview.quota_deduction)} روز کاری</strong> از سهمیه کسر می‌شود)
        </p>
      ) : null}

      {duration === 'multiple' && preview ? (
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
          {preview.total_days_requested != null ? (
            <p className="mb-0">
              تعداد کل روزهای تقویمی: <strong>{formatFaNum(preview.total_days_requested)} روز</strong>
            </p>
          ) : null}
          <p className="text-emerald-700 dark:text-emerald-400 font-semibold mb-0">
            تعداد روزهای کاری منظور شده:{' '}
            <strong>{formatFaNum(preview.working_days_count ?? preview.quota_deduction)} روز</strong>
          </p>
          {excludedOffs.length > 0 ? (
            <div className="text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 p-2 rounded text-xs mt-2">
              روزهای تعطیل شیفت/هفتگی که از مرخصی حذف شدند:
              <ul className="list-disc pr-4 mt-1 mb-0">
                {excludedOffs.map((item, idx) => (
                  <li key={`off-${idx}`}>
                    {item.date}
                    {item.reason ? ` (${item.reason})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {excludedHolidays.length > 0 ? (
            <div className="text-rose-800 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 p-2 rounded text-xs mt-2">
              تعطیلات رسمی حذف‌شده:
              <ul className="list-disc pr-4 mt-1 mb-0">
                {excludedHolidays.map((item, idx) => (
                  <li key={`hol-${idx}`}>
                    {item.date}
                    {item.reason ? ` (${item.reason})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      {preview && duration !== 'hourly' && duration !== 'multiple' && preview.quota_deduction != null ? (
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
          کسر از سهمیه: <strong>{formatFaNum(preview.quota_deduction)} روز</strong>
        </p>
      ) : null}

      {(policies.min_advance_days != null ||
        policies.max_consecutive_days != null ||
        policies.requires_file != null ||
        policies.requires_approval != null) && (
        <div className="text-xs text-slate-600 dark:text-slate-300 bg-white/60 dark:bg-black/20 rounded p-2 mt-2 space-y-0.5">
          {policies.min_advance_days != null && Number(policies.min_advance_days) > 0 ? (
            <div>
              حداقل درخواست قبل از مرخصی:{' '}
              <strong>{formatFaNum(policies.min_advance_days)} روز</strong>
              {policies.days_in_advance != null
                ? ` (فاصله فعلی: ${formatFaNum(policies.days_in_advance)} روز)`
                : ''}
            </div>
          ) : null}
          {policies.max_consecutive_days != null ? (
            <div>
              حداکثر روز متوالی مجاز: <strong>{formatFaNum(policies.max_consecutive_days)}</strong>
            </div>
          ) : null}
          {policies.requires_file ? (
            <div className="text-amber-700">پیوست مدرک برای این نوع مرخصی الزامی است</div>
          ) : null}
          {policies.requires_approval === false ? (
            <div className="text-emerald-700">تأیید خودکار — بدون نیاز به تأیید مدیر</div>
          ) : (
            <div>پس از ثبت، در انتظار تأیید مدیر خواهد بود</div>
          )}
        </div>
      )}

      <div className="mt-2 pt-2 border-t border-blue-100 dark:border-blue-900 flex flex-wrap justify-between gap-2 text-xs text-gray-600 dark:text-gray-400">
        <span>
          مانده سهمیه فعلی: {formatFaNum(remaining)} روز
          {afterLeave != null ? ` → پس از ثبت: ${formatFaNum(afterLeave)}` : ''}
        </span>
        {preview ? (
          <span className={preview.allowed ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
            {preview.allowed
              ? 'مجاز به ثبت'
              : preview.message || 'بیش از حد مجاز سهمیه / نقض سیاست'}
          </span>
        ) : null}
      </div>
      {preview?.message && preview.allowed === false ? (
        <div className="text-xs text-red-600 mt-2">{preview.message}</div>
      ) : null}
    </div>
  );
};

const LeaveForm = ({
  onSave,
  onCancel,
  loading = false,
  initialValues = null,
  submitLabel = 'ذخیره',
}) => {
  const [form] = Form.useForm();
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveTypesLoading, setLeaveTypesLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [duration, setDuration] = useState('single');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const previewTimer = useRef(null);

  const durationWatch = Form.useWatch('duration', form) || duration;
  const leaveTypeWatch = Form.useWatch('leaveType', form);

  const requiresFile = Boolean(
    preview?.requires_file ??
      preview?.policies?.requires_file ??
      selectedPolicy?.requires_file,
  );
  const requiresApproval =
    (preview?.requires_approval ??
      preview?.policies?.requires_approval ??
      selectedPolicy?.requires_approval) !== false;

  useEffect(() => {
    const loadEmployees = async () => {
      setEmployeesLoading(true);
      try {
        const res = await employeesApi.getAll();
        const employeesList = (res.data || []).map((emp) => ({
          id: emp.id,
          name: emp.name || '--',
        }));
        setEmployees(employeesList);
      } catch (error) {
        console.error('خطا در دریافت لیست کارمندان:', error);
        message.error('خطا در دریافت لیست کارمندان');
      } finally {
        setEmployeesLoading(false);
      }
    };
    loadEmployees();
  }, []);

  useEffect(() => {
    if (!initialValues) return;
    const d = initialValues.duration || 'single';
    setDuration(d);
    setSelectedEmployee(initialValues.member || initialValues.user_id || null);
    form.setFieldsValue({
      member: initialValues.member ?? initialValues.user_id,
      leaveType: initialValues.leaveType ?? initialValues.leave_type_id,
      status: initialValues.status || 'pending',
      duration: d,
      date: initialValues.date || null,
      dateRange: initialValues.dateRange || [],
      start_time: initialValues.start_time
        ? dayjs(toLatinDigits(String(initialValues.start_time)).slice(0, 5), 'HH:mm')
        : null,
      end_time: initialValues.end_time
        ? dayjs(toLatinDigits(String(initialValues.end_time)).slice(0, 5), 'HH:mm')
        : null,
      reason: initialValues.reason || '',
    });
    if (initialValues.member || initialValues.user_id) {
      const uid = initialValues.member || initialValues.user_id;
      loadLeaveTypes(uid).then(() => {
        // policy بعد از load در leaveTypes ست می‌شود — در effect جدا
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  useEffect(() => {
    if (!leaveTypeWatch || !leaveTypes.length) return;
    const type = leaveTypes.find((t) => Number(t.id) === Number(leaveTypeWatch));
    if (type) setSelectedPolicy(type);
  }, [leaveTypeWatch, leaveTypes]);

  const loadLeaveTypes = async (employeeId) => {
    if (!employeeId) {
      setLeaveTypes([]);
      return;
    }
    setLeaveTypesLoading(true);
    try {
      const res = await leavesApi.getEmployeeLeaveTypes(employeeId);
      const types = res.data?.leave_types || [];
      setLeaveTypes(types);
    } catch (error) {
      console.error('خطا در دریافت انواع مرخصی:', error);
      message.error('خطا در دریافت انواع مرخصی');
      setLeaveTypes([]);
    } finally {
      setLeaveTypesLoading(false);
    }
  };

  const handleEmployeeChange = async (employeeId) => {
    setSelectedEmployee(employeeId);
    form.setFieldsValue({ leaveType: undefined });
    setSelectedPolicy(null);
    setPreview(null);
    await loadLeaveTypes(employeeId);
  };

  const handleLeaveTypeChange = (leaveTypeId) => {
    const type = leaveTypes.find((t) => Number(t.id) === Number(leaveTypeId));
    setSelectedPolicy(type || null);
    if (type) {
      // اگر تأیید خودکار باشد، وضعیت را approved می‌گذاریم؛ در غیر این صورت pending
      if (type.requires_approval === false) {
        form.setFieldsValue({ status: 'approved' });
      } else {
        form.setFieldsValue({ status: 'pending' });
      }
    }
    setPreview(null);
  };

  const buildPreviewPayload = useCallback((values) => {
    const d = values.duration || 'single';
    const userId = values.member;
    const leaveTypeId = values.leaveType;
    if (!userId || !leaveTypeId || !d) return null;

    const base = {
      user_id: userId,
      leave_type_id: leaveTypeId,
      duration: d,
    };

    if (d === 'hourly') {
      const leaveDate = toApiDate(values.date);
      const start = toApiTime(values.start_time);
      const end = toApiTime(values.end_time);
      if (!leaveDate || !start || !end) return null;
      return { ...base, leave_date: leaveDate, start_time: start, end_time: end };
    }

    if (d === 'multiple') {
      let start = null;
      let end = null;
      if (Array.isArray(values.dateRange) && values.dateRange.length >= 2) {
        start = toApiDate(values.dateRange[0]);
        end = toApiDate(values.dateRange[1]);
      }
      if (!start || !end) return null;
      return { ...base, multiStartDate: start, multiEndDate: end };
    }

    const leaveDate = toApiDate(values.date);
    if (!leaveDate) return null;
    return { ...base, leave_date: leaveDate };
  }, []);

  const runPreview = useCallback(
    (values) => {
      if (previewTimer.current) clearTimeout(previewTimer.current);
      previewTimer.current = setTimeout(async () => {
        const payload = buildPreviewPayload(values);
        if (!payload) {
          setPreview(null);
          setPreviewError(null);
          return;
        }
        setPreviewLoading(true);
        setPreviewError(null);
        try {
          const res = await leavesApi.calculateDays(payload);
          const data = res?.data ?? res ?? null;
          setPreview(data);
        } catch (err) {
          console.error('Leave preview failed:', err);
          setPreview(null);
          setPreviewError(err?.message || 'خطا در محاسبه پیش‌نمایش مرخصی');
        } finally {
          setPreviewLoading(false);
        }
      }, 400);
    },
    [buildPreviewPayload],
  );

  useEffect(
    () => () => {
      if (previewTimer.current) clearTimeout(previewTimer.current);
    },
    [],
  );

  const handleValuesChange = (_changed, allValues) => {
    if (allValues.duration && allValues.duration !== duration) {
      setDuration(allValues.duration);
    }
    runPreview(allValues);
  };

  const handleDurationChange = (e) => {
    const next = e.target.value;
    setDuration(next);
    setPreview(null);
    // پاک‌سازی فیلدهای وابسته
    if (next === 'multiple') {
      form.setFieldsValue({ date: undefined, start_time: undefined, end_time: undefined });
    } else if (next === 'hourly') {
      form.setFieldsValue({ dateRange: undefined });
    } else {
      form.setFieldsValue({
        dateRange: undefined,
        start_time: undefined,
        end_time: undefined,
      });
    }
  };

  const handleSubmit = async (values) => {
    if (!onSave) return;
    const payload = buildLeavePayload(values);

    // وضعیت بر اساس پالیسی تأیید
    if (selectedPolicy?.requires_approval === false || preview?.requires_approval === false) {
      payload.status = 'approved';
    }

    if (needsTimeRange(values.duration)) {
      if (!payload.start_time || !payload.end_time) {
        message.error('ساعت شروع و پایان الزامی است');
        return;
      }
      if (payload.start_time >= payload.end_time) {
        message.error('ساعت پایان باید بعد از ساعت شروع باشد');
        return;
      }
    }
    if (needsDateRange(values.duration)) {
      if (!payload.multiStartDate || !payload.multiEndDate) {
        message.error('تاریخ شروع و پایان بازه الزامی است');
        return;
      }
    }
    if (needsSingleDate(values.duration) && !payload.leave_date) {
      message.error('تاریخ مرخصی الزامی است');
      return;
    }

    const fileList = values.file?.fileList || values.file;
    const fileObj = Array.isArray(fileList)
      ? fileList[0]?.originFileObj || fileList[0]
      : fileList?.originFileObj || null;

    if (requiresFile && !fileObj) {
      message.error('پیوست مدرک برای این نوع مرخصی الزامی است');
      return;
    }

    if (preview && preview.allowed === false) {
      message.warning(preview.message || 'ثبت این مرخصی بیش از سهمیه مجاز است یا سیاست نقض شده');
      return;
    }

    // با فایل → FormData ؛ بدون فایل → JSON
    if (fileObj) {
      const fd = new FormData();
      Object.entries(payload).forEach(([key, val]) => {
        if (val === undefined || val === null) return;
        fd.append(key, typeof val === 'boolean' ? (val ? '1' : '0') : String(val));
      });
      fd.append('file', fileObj);
      await onSave(fd);
    } else {
      await onSave(payload);
    }
  };

  const handleFormFinish = async (values) => {
    try {
      await handleSubmit(values);
    } catch (error) {
      handleFormSubmitError(form, error);
    }
  };

  const uploadProps = {
    beforeUpload: () => false,
    maxCount: 1,
  };

  const showSingleDate = needsSingleDate(durationWatch);
  const showRange = needsDateRange(durationWatch);
  const showTimes = needsTimeRange(durationWatch);

  const submitDisabled = useMemo(
    () => Boolean(preview && preview.allowed === false),
    [preview],
  );

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFormFinish}
      onValuesChange={handleValuesChange}
      scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
      className="space-y-6"
      style={{ fontFamily: 'CustomFont' }}
      initialValues={{
        status: 'pending',
        duration: 'single',
      }}
    >
      <Card className="bg-white dark:bg-gray-800">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="انتخاب عضو"
              name="member"
              rules={[{ required: true, message: 'لطفاً عضو را انتخاب کنید' }]}
            >
              <Select
                placeholder="کارمند را انتخاب کنید"
                style={{ fontFamily: 'CustomFont' }}
                loading={employeesLoading}
                showSearch
                optionFilterProp="children"
                onChange={handleEmployeeChange}
              >
                {employees.map((emp) => (
                  <Option key={emp.id} value={emp.id}>
                    {emp.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="نوع مرخصی"
              name="leaveType"
              rules={[{ required: true, message: 'لطفاً نوع مرخصی را انتخاب کنید' }]}
            >
              <Select
                placeholder={
                  selectedEmployee ? 'نوع مرخصی را انتخاب کنید' : 'ابتدا کارمند را انتخاب کنید'
                }
                style={{ fontFamily: 'CustomFont' }}
                loading={leaveTypesLoading}
                disabled={!selectedEmployee}
                onChange={handleLeaveTypeChange}
              >
                {leaveTypes.map((type) => (
                  <Option key={type.id} value={type.id}>
                    {type.name}{' '}
                    {type.remaining !== undefined ? `(باقی‌مانده: ${type.remaining})` : ''}
                    {type.requires_file ? ' · مدرک' : ''}
                    {type.requires_approval === false ? ' · تأیید خودکار' : ''}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label={
                <span>
                  وضعیت
                  {!requiresApproval ? (
                    <span className="text-xs text-emerald-600 mr-2">(تأیید خودکار)</span>
                  ) : null}
                </span>
              }
              name="status"
            >
              <Select style={{ fontFamily: 'CustomFont' }} disabled={!requiresApproval}>
                <Option value="pending">در انتظار</Option>
                <Option value="approved">تایید شده</Option>
                <Option value="rejected">رد شده</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              label="مدت مرخصی"
              name="duration"
              rules={[{ required: true, message: 'مدت مرخصی را انتخاب کنید' }]}
            >
              <Radio.Group onChange={handleDurationChange} className="custom-font">
                {LEAVE_DURATION_OPTIONS.map((opt) => (
                  <Radio key={opt.value} value={opt.value} style={{ fontFamily: 'CustomFont' }}>
                    {opt.label}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          </Col>

          {showSingleDate ? (
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={
                  <span className="inline-flex items-center gap-1">
                    <CalendarOutlined />
                    تاریخ مرخصی
                  </span>
                }
                name="date"
                rules={[{ required: true, message: 'لطفاً تاریخ را انتخاب کنید' }]}
              >
                <PersianDatePicker placeholder="تاریخ را انتخاب کنید" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          ) : null}

          {showTimes ? (
            <>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  label={
                    <span className="inline-flex items-center gap-1">
                      <ClockCircleOutlined />
                      از ساعت
                    </span>
                  }
                  name="start_time"
                  rules={[{ required: true, message: 'ساعت شروع الزامی است' }]}
                >
                  <TimePicker
                    format="HH:mm"
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    placeholder="09:00"
                    needConfirm={false}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  label={
                    <span className="inline-flex items-center gap-1">
                      <ClockCircleOutlined />
                      تا ساعت
                    </span>
                  }
                  name="end_time"
                  rules={[
                    { required: true, message: 'ساعت پایان الزامی است' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const start = getFieldValue('start_time');
                        if (!value || !start) return Promise.resolve();
                        const s = toApiTime(start);
                        const e = toApiTime(value);
                        if (s && e && e > s) return Promise.resolve();
                        return Promise.reject(new Error('ساعت پایان باید بعد از ساعت شروع باشد'));
                      },
                    }),
                  ]}
                >
                  <TimePicker
                    format="HH:mm"
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    placeholder="13:00"
                    needConfirm={false}
                  />
                </Form.Item>
              </Col>
            </>
          ) : null}

          {showRange ? (
            <Col xs={24} sm={16} md={12}>
              <Form.Item
                label={
                  <span className="inline-flex items-center gap-1">
                    <CalendarOutlined />
                    بازه تاریخ (شروع تا پایان)
                  </span>
                }
                name="dateRange"
                rules={[
                  {
                    validator(_, value) {
                      if (Array.isArray(value) && value.length >= 2 && value[0] && value[1]) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('تاریخ شروع و پایان را انتخاب کنید'));
                    },
                  },
                ]}
              >
                <PersianDatePicker
                  range
                  placeholder="از تاریخ — تا تاریخ"
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <div className="text-xs text-slate-500 -mt-3 mb-2 custom-font">
                سیستم به‌صورت خودکار تعطیلات رسمی و روزهای تعطیل شیفت را از سهمیه حذف می‌کند.
              </div>
            </Col>
          ) : null}

          <Col xs={24}>
            {(preview || previewLoading || previewError || selectedPolicy) && (
              <div className="mb-2">
                {previewError ? (
                  <Alert type="warning" showIcon className="custom-font" message={previewError} />
                ) : (
                  <LeavePreviewBox
                    preview={preview}
                    duration={durationWatch}
                    loading={previewLoading}
                    policy={selectedPolicy}
                  />
                )}
              </div>
            )}
          </Col>

          <Col xs={24}>
            <Form.Item
              label="دلیل غیبت"
              name="reason"
              rules={[{ required: true, message: 'لطفاً دلیل غیبت را وارد کنید' }]}
            >
              <TextArea
                rows={4}
                placeholder="مثلاً: مراجعه به بانک / سفر خانوادگی"
                style={{ fontFamily: 'CustomFont' }}
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              label={
                <span>
                  افزودن فایل{' '}
                  {requiresFile ? (
                    <span className="text-red-500 text-xs">(الزامی برای این نوع مرخصی)</span>
                  ) : (
                    <Tooltip title="می‌توانید رسید ویزیت پزشک، نسخه یا مدارک سفر را ضمیمه کنید.">
                      <QuestionCircleOutlined className="text-gray-400" />
                    </Tooltip>
                  )}
                </span>
              }
              name="file"
              rules={
                requiresFile
                  ? [
                      {
                        validator(_, value) {
                          const list = value?.fileList || value;
                          const has =
                            (Array.isArray(list) && list.length > 0) ||
                            Boolean(value?.originFileObj);
                          if (has) return Promise.resolve();
                          return Promise.reject(new Error('پیوست مدرک الزامی است'));
                        },
                      },
                    ]
                  : undefined
              }
            >
              <Upload.Dragger
                {...uploadProps}
                className="custom-upload"
                style={{ fontFamily: 'CustomFont' }}
                accept=".pdf,.jpg,.jpeg,.png,.webp"
              >
                <p className="ant-upload-drag-icon">
                  <UploadOutlined />
                </p>
                <p className="ant-upload-text" style={{ fontFamily: 'CustomFont' }}>
                  {requiresFile ? 'فایل مدرک را انتخاب کنید (الزامی)' : 'یک فایل انتخاب کنید'}
                </p>
              </Upload.Dragger>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <div className="flex items-center justify-end gap-4 pt-4">
        <Button onClick={onCancel} size="middle" style={{ fontFamily: 'CustomFont' }}>
          لغو
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          size="middle"
          loading={loading}
          disabled={submitDisabled}
          className="bg-green-600 hover:bg-green-700 border-green-600"
          style={{ fontFamily: 'CustomFont' }}
        >
          {submitLabel}
        </Button>
      </div>
    </Form>
  );
};

export default LeaveForm;
